// src/components/steps/Step1Setup.jsx
//
// Wizard step 1 — the setup form (mode, platform / format, parameters, sources).
// Receives state + callbacks as props. Output types the plan does not grant are
// greyed with an explanatory sentence (plus a "contact your administrator" line on
// a blocked reason) — never an upgrade link.
import React, { useRef, useState, useCallback, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { MODES, ESC_ACCENT, ICON_MAP } from "../../constants/modes.js";
import { COLORS, FONT_MONO, FONT_SANS, RADII, SEMANTIC, SHADOW } from '../../constants/theme.js';
import {
  TONE_OPTIONS,
  POST_LENGTH_OPTIONS,
  CTA_TYPE_OPTIONS,
  CAROUSEL_SLIDE_OPTIONS,
  BLOG_LENGTH_OPTIONS,
  SEO_INTENT_OPTIONS,
  EMAIL_LENGTH_OPTIONS,
  SUBJECT_STYLE_OPTIONS,
  ARTICLE_STYLE_OPTIONS,
  ADS_CHANNEL_OPTIONS,
  CASE_STUDY_SECTION_OPTIONS,
  CASE_STUDY_FOCUS_OPTIONS,
  LANDING_HEADLINE_OPTIONS,
} from "../../constants/options.js";
import { SOCIAL_CHANNELS, EMAIL_CHANNELS } from "../../constants/channels.js";
import { normalizeUrl } from "../../utils/textProcessing.js";
import { translateOptions, resolveToneLabel } from "../../utils/optionLabels.js";
import {
  ContentTypeIcon,
  BriefIcon,
  ParametriIcon,
  FontiIcon,
  MODE_ICONS,
} from "../../constants/sectionIcons.jsx";
import {
  Target,
  Calendar,
  Layers,
  BookOpen,
  Upload,
  X,
  FileText,
  HelpCircle,
  Download,
  ChevronDown,
  Check,
  Globe,
  Lock,
} from "lucide-react";
import useOutputEntitlements, {
  REFUSAL_REASONS,
} from "../../hooks/useOutputEntitlements.js";
import {
  isModeAllowed,
  isSelectionAllowed,
  isCurrentSelectionAllowed,
  modeSelections,
} from "../../utils/wizardFormats.js";
import {
  getBundle,
  uploadFileToBundle,
  deleteBundleFile,
} from "../../services/api/uploadClient.js";
import {
  importParamsFromFile,
  ImportParamsError,
} from "../../utils/importParamsFromFile.js";

// ── Tooltip component ───────────────────────────────────────────────────────
const Tooltip = ({ text, color }) => {
  const [visible, setVisible] = useState(false);
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        cursor: "help",
      }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <HelpCircle
        size={13}
        style={{
          color: color || "#888",
          opacity: 0.6,
          transition: "opacity 0.15s",
        }}
      />
      {visible && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 8px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#1a1a2e",
            color: COLORS.borderStrong,
            fontSize: "12px",
            lineHeight: 1.5,
            padding: "10px 14px",
            borderRadius: "10px",
            whiteSpace: "normal",
            width: "260px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
            border: "1px solid rgba(255,255,255,0.1)",
            zIndex: 1000,
            pointerEvents: "none",
            fontWeight: 400,
            fontFamily: FONT_SANS,
            letterSpacing: "normal",
            textTransform: "none",
          }}
        >
          {text}
          <span
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              borderTop: "6px solid #1a1a2e",
            }}
          />
        </span>
      )}
    </span>
  );
};

// Inline lock marker for the segmented controls (format / channel pickers).
// Module scope so it keeps its identity across renders.
const LockMark = () => (
  <Lock size={11} style={{ marginRight: "5px", verticalAlign: "-1px" }} />
);

const ALLOWED_EXTENSIONS = [".pdf", ".docx", ".pptx", ".xlsx", ".txt", ".md"];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

// Oltre questa altezza la textarea smette di crescere e scrolla (≈10 righe).
const AUTO_GROW_MAX_HEIGHT = 240;

const AutoGrowTextarea = ({ value, onChange, placeholder, style, maxHeight }) => {
  const ref = useRef(null);
  useLayoutEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    const full = ref.current.scrollHeight;
    const capped = maxHeight ? Math.min(full, maxHeight) : full;
    ref.current.style.height = capped + "px";
    // Scrollbar solo quando il testo supera il tetto: sotto il tetto la
    // textarea è alta esattamente quanto il contenuto.
    ref.current.style.overflowY = maxHeight && full > maxHeight ? "auto" : "hidden";
  }, [value, maxHeight]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={1}
      style={style}
    />
  );
};

const Step1Setup = ({
  state,
  dispatch,
  color,
  w,
  t,
  styles = {},
  personaOptions = [],
  objectiveOptions = [],
  toneOptions = TONE_OPTIONS,
}) => {
  const { t: tr } = useTranslation("wizard");
  // Map an options array to translated labels under options.<group>.<value>.
  // Falls back to the option's own label if no translation key exists
  // (e.g. backend-provided custom personas/objectives).
  const trOptions = (group, opts) => translateOptions(tr, group, opts);
  const {
    activeMode,
    focusTopic,
    taskText,
    taskContract,
    webSearch,
    kbSearch,
    persona,
    tone,
    customTone,
    objective,
    ctaUrl,
    ctaText,
    referenceUrls,
    refsEnabled,
    userKbEnabled,
    userKbQuery,
    linkedInParams,
    carouselParams,
    blogParams,
    emailParams,
    adsParams,
    caseStudyParams,
    landingParams,
    contentFormat,
    socialChannel,
    emailChannel,
    uploadBundle,
  } = state;
  const c = color || ESC_ACCENT;

  const set = (key, val) => dispatch({ type: "SET_KEY", key, value: val });

  // ─── Plan entitlements — UX pre-flight on the option list ────────────────
  // `variants[]` from GET /api/v1/entitlements decides which output types are
  // OFFERED. This is not enforcement: the server re-runs the gate on the run and
  // answers 402 FEATURE_NOT_IN_PLAN.
  //
  // ⛔⛔ `variants === null` and `variants === []` are DIFFERENT:
  //     null → not known (loading, no tenant, a failed read, an older backend)
  //            → offer everything. A lookup blink must never blank the catalogue.
  //     []   → nothing granted → grey everything and say WHY (`reason`).
  //
  // ⚠️ `reason` is about the APPLICATION, not one output type: FEATURE_NOT_IN_PLAN
  // here means the plan does not buy the generator at all. A per-output-type
  // refusal only ever arrives as the 402 on the run.
  const { variants, reason, stale } = useOutputEntitlements();
  const modeLocked = (id) => !isModeAllowed(variants, id);
  const selectionLocked = (sel) => !isSelectionAllowed(variants, sel);
  // 4.4: the *selected* combination stopped being sold (tenant switch, plan
  // change). Surfaced as a notice — never by rewriting the user's selection.
  const currentSelectionLocked = !isCurrentSelectionAllowed(variants, state);
  const anyOptionLocked =
    variants != null &&
    MODES.some((m) => modeSelections(m.id).some((sel) => selectionLocked(sel)));
  // Nothing is granted AND we know why — this outranks both hints below.
  const blockedReason = reason && REFUSAL_REASONS.has(reason) ? reason : null;
  const lockedHint = tr("step1.platform.entitlements.lockedTitle");

  // ─── "Fonti prioritarie" — merged upload + reference-URL source ──────────
  // One row/toggle drives both underlying wire flags (userKbEnabled = uploaded
  // docs, refsEnabled = reference URLs). buildBriefRequest reads them
  // independently with empty-filters, so keeping both keys is ZERO-BREAK.
  const priorityOn = userKbEnabled || refsEnabled;
  const togglePriority = () => {
    const next = !priorityOn;
    set("userKbEnabled", next);
    set("refsEnabled", next);
  };

  // ─── Blog-SEO keywords mutators ─────────────────────────────────────────
  const setKeywords = (newKws) =>
    set("blogParams", { ...blogParams, keywords: newKws });
  const setPrimaryKeyword = (val) =>
    setKeywords({ ...blogParams.keywords, primary: val });
  const setSecondarySlot = (idx, val) => {
    const next = [...blogParams.keywords.secondary];
    while (next.length <= idx)
      next.push({ keyword: "", monthly_volume: null });
    next[idx] = { ...next[idx], keyword: val };
    setKeywords({ ...blogParams.keywords, secondary: next });
  };

  const fileInputRef = useRef(null);
  const importInputRef = useRef(null);

  const [dragOver, setDragOver] = useState(false);
  const [importStatus, setImportStatus] = useState(null); // { type: 'ok' | 'err', msg }
  const [includeCta, setIncludeCta] = useState(
    Boolean((ctaText && ctaText.trim()) || (ctaUrl && ctaUrl.trim())),
  );
  const toggleCta = () =>
    setIncludeCta((prev) => {
      const next = !prev;
      if (!next) {
        // OFF → clear so the pipeline sends no CTA
        set("ctaText", "");
        set("ctaUrl", "");
        set("linkedInParams", {
          ...linkedInParams,
          ctaType: CTA_TYPE_OPTIONS[0].value,
        });
      }
      return next;
    });

  const handleImportSelect = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) return;
      setImportStatus(null);
      try {
        const formState = await importParamsFromFile(file);
        dispatch({ type: "SET_BATCH", payload: formState });
        setImportStatus({
          type: "ok",
          msg: tr("step1.topic.importedStatus", { name: file.name }),
        });
      } catch (err) {
        const msg =
          err instanceof ImportParamsError
            ? err.message
            : tr("step1.topic.importError", { message: err.message || err });
        setImportStatus({ type: "err", msg });
      }
    },
    [dispatch, tr],
  );

  const processFile = useCallback(
    async (file) => {
      if (!file) return;
      const ext = file.name.slice(file.name.lastIndexOf(".")).toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        dispatch({
          type: "SET_UPLOAD_BUNDLE_ERROR",
          error: tr("step1.sources.upload.fileTypeError", {
            ext,
            allowed: ALLOWED_EXTENSIONS.join(", "),
          }),
        });
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        dispatch({
          type: "SET_UPLOAD_BUNDLE_ERROR",
          error: tr("step1.sources.upload.fileSizeError", {
            size: (file.size / 1024 / 1024).toFixed(1),
          }),
        });
        return;
      }
      dispatch({ type: "SET_UPLOAD_BUNDLE_LOADING", value: true });
      try {
        await uploadFileToBundle(file);
        const bundle = await getBundle();
        dispatch({
          type: "SET_UPLOAD_BUNDLE_FILES",
          files: bundle.files || [],
        });
      } catch (err) {
        dispatch({ type: "SET_UPLOAD_BUNDLE_ERROR", error: err.message });
      }
    },
    [dispatch, tr],
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    processFile(file);
  };

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const file = e.dataTransfer.files?.[0];
      processFile(file);
    },
    [processFile],
  );

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);
  const handleDragLeave = useCallback(() => setDragOver(false), []);

  const handleDeleteFile = async (filename) => {
    dispatch({ type: "SET_UPLOAD_BUNDLE_LOADING", value: true });
    try {
      await deleteBundleFile(filename);
      const bundle = await getBundle();
      dispatch({ type: "SET_UPLOAD_BUNDLE_FILES", files: bundle.files || [] });
    } catch (err) {
      dispatch({ type: "SET_UPLOAD_BUNDLE_ERROR", error: err.message });
    }
  };

  const inp = {
    width: "100%",
    padding: "12px 16px",
    background: t.inputBg,
    border: `1px solid ${t.inputBorder}`,
    borderRadius: RADII.control,
    color: t.text,
    fontSize: "14px",
    fontFamily: FONT_SANS,
    outline: "none",
    boxSizing: "border-box",
  };
  // [FIX-v161] Lo stile `lbl` (10px mono, uppercase forzato) è stato rimosso:
  // le sue uniche quattro occorrenze (Social Proof + le tre label del blocco
  // keywords) sono ora su `fieldLbl`, così tutte le label della card Parametri
  // condividono dimensione, peso e capitalizzazione.
  const sec = {
    background: t.surface,
    border: `1px solid ${t.border}`,
    borderRadius: RADII.section,
    padding: "24px",
    marginBottom: "16px",
    boxShadow: SHADOW.card,
    fontFamily: FONT_SANS,
  };
  const fieldLbl = {
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 600,
    color: t.text,
    fontFamily: FONT_SANS,
    marginBottom: "8px",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };

  const SectionHeader = ({ icon: Icon, title, subtitle }) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        marginBottom: "18px",
      }}
    >
      <div
        style={{
          width: "40px",
          height: "40px",
          borderRadius: RADII.pill,
          flexShrink: 0,
          border: `1px solid ${t.border}`,
          background: t.surface,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} color={t.text} strokeWidth={1.8} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: "16px",
            fontWeight: 700,
            color: t.text,
            fontFamily: FONT_SANS,
            lineHeight: "20px",
          }}
        >
          {title}
        </div>
        {subtitle && (
          <div
            style={{
              fontSize: "12px",
              color: t.textFaint,
              fontFamily: FONT_SANS,
              marginTop: "2px",
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
    </div>
  );


  const Toggle = ({ on, onToggle }) => (
    <div
      onClick={onToggle}
      role="switch"
      aria-checked={on}
      style={{
        width: "44px",
        height: "24px",
        borderRadius: RADII.pill,
        flexShrink: 0,
        cursor: "pointer",
        background: on ? c : t.border,
        position: "relative",
        transition: "background 0.2s",
      }}
    >
      <div
        style={{
          width: "18px",
          height: "18px",
          borderRadius: "50%",
          background: COLORS.surface,
          position: "absolute",
          top: "3px",
          left: on ? "23px" : "3px",
          transition: "left 0.2s",
        }}
      />
    </div>
  );

  const SourceRow = ({ sem, icon: Icon, title, desc, on, onToggle, children }) => (
    <div
      style={{
        border: `1px solid ${t.border}`,
        borderRadius: RADII.source,
        padding: "16px",
        marginBottom: "12px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
        <div
          style={{
            width: "40px",
            height: "40px",
            borderRadius: RADII.control,
            flexShrink: 0,
            background: sem.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon size={20} color={sem.fg} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: "14px",
              fontWeight: 600,
              color: t.text,
              fontFamily: FONT_SANS,
            }}
          >
            {title}
          </div>
          <div
            style={{
              fontSize: "12px",
              color: t.textFaint,
              fontFamily: FONT_SANS,
              marginTop: "2px",
            }}
          >
            {desc}
          </div>
        </div>
        <Toggle on={on} onToggle={onToggle} />
      </div>
      {on && children && <div style={{ marginTop: "14px" }}>{children}</div>}
    </div>
  );

  const LengthSlider = ({ label, options, value, onChange }) => {
    const idx = Math.max(
      0,
      options.findIndex((o) => o.value === value),
    );
    const pct = options.length > 1 ? (idx / (options.length - 1)) * 100 : 0;
    return (
      <div>
        <label style={fieldLbl}>{label}</label>
        <input
          type="range"
          className="brian-range"
          min={0}
          max={options.length - 1}
          step={1}
          value={idx}
          onChange={(e) => onChange(options[Number(e.target.value)].value)}
          style={{
            width: "100%",
            height: "8px",
            borderRadius: RADII.pill,
            appearance: "none",
            cursor: "pointer",
            background: `linear-gradient(to right, ${c} 0%, ${c} ${pct}%, ${t.inputBg} ${pct}%, ${t.inputBg} 100%)`,
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            marginTop: "8px",
          }}
        >
          {options.map((o, i) => {
            // Labels arrive as "Breve (80-130 parole)" — split into name + range so
            // they render on two lines like the design.
            const m = /^(.*?)\s*\((.*)\)\s*$/.exec(o.label || "");
            const name = m ? m[1] : o.label;
            const range = m ? m[2] : null;
            const align =
              i === 0 ? "left" : i === options.length - 1 ? "right" : "center";
            return (
              <div
                key={o.value}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "2px",
                  textAlign: align,
                  fontFamily: FONT_SANS,
                }}
              >
                <span style={{ fontSize: "12px", fontWeight: 600, color: t.text }}>
                  {name}
                </span>
                {range && (
                  <span style={{ fontSize: "12px", color: t.textFaint }}>
                    {range}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <style>{`
          .brian-range::-webkit-slider-thumb { -webkit-appearance:none; width:18px; height:18px; border-radius:999px; background:${c}; border:2px solid ${COLORS.surface}; cursor:pointer; }
          .brian-range::-moz-range-thumb { width:18px; height:18px; border-radius:999px; background:${c}; border:2px solid ${COLORS.surface}; cursor:pointer; }
        `}</style>
      </div>
    );
  };

  const Sel = ({ label, helper, value, onChange, options }) => (
    <div>
      <label style={fieldLbl}>{label}</label>
      <div style={{ position: "relative" }}>
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{
            ...inp,
            appearance: "none",
            cursor: "pointer",
            paddingRight: "40px",
          }}
        >
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <ChevronDown
          size={18}
          color={t.textFaint}
          style={{
            position: "absolute",
            right: "14px",
            top: "50%",
            transform: "translateY(-50%)",
            pointerEvents: "none",
          }}
        />
      </div>
      {helper && (
        <div
          style={{
            fontSize: "12px",
            color: t.textMuted,
            marginTop: "6px",
            lineHeight: 1.5,
            fontFamily: FONT_SANS,
          }}
        >
          {helper}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* 5: Platform Selection */}
      <div id="cg-tipo" style={sec}>
        <SectionHeader
          icon={ContentTypeIcon}
          title={tr("step1.platform.label")}
          subtitle={tr("step1.platform.subtitle")}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: "10px",
          }}
        >
          {MODES.map((mode) => {
            const selected = activeMode === mode.id;
            // A mode locks only when EVERY variant behind it is refused; a
            // partially sold mode stays clickable and locks its own variants
            // on the segmented control below.
            const planLocked = modeLocked(mode.id);
            const blocked = mode.disabled || planLocked;
            const ModeIcon =
              MODE_ICONS[mode.id] || ICON_MAP[mode.iconId] || FileText;
            return (
              <button
                key={mode.id}
                disabled={blocked}
                onClick={
                  blocked
                    ? undefined
                    : () => {
                        set("activeMode", mode.id);
                        set("contentFormat", "post");
                        set("emailChannel", "email-classic");
                      }
                }
                title={
                  mode.disabled
                    ? tr("step1.platform.disabledTitle")
                    : planLocked
                      ? lockedHint
                      : undefined
                }
                style={{
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  minHeight: "76px",
                  padding: "12px 8px",
                  borderRadius: RADII.card,
                  border: selected
                    ? `1px solid ${c}`
                    : `1px solid ${t.border}`,
                  background: selected ? COLORS.accentSoft : t.surface,
                  color: selected ? c : t.textMuted,
                  fontSize: "12px",
                  fontWeight: 600,
                  fontFamily: FONT_SANS,
                  cursor: blocked ? "not-allowed" : "pointer",
                  transition: "all 0.15s",
                  opacity: blocked ? 0.5 : 1,
                }}
              >
                <ModeIcon size={20} />
                {tr(`modes.${mode.id}.name`)}
                {mode.disabled && (
                  <span
                    style={{
                      display: "block",
                      fontSize: "9px",
                      fontFamily: FONT_MONO,
                      color: t.textFaint,
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    {tr("step1.platform.comingSoon")}
                  </span>
                )}
                {!mode.disabled && planLocked && (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "4px",
                      fontSize: "9px",
                      fontFamily: FONT_MONO,
                      color: t.textFaint,
                      letterSpacing: "0.8px",
                      textTransform: "uppercase",
                      fontWeight: 600,
                    }}
                  >
                    <Lock size={10} />
                    {tr("step1.platform.entitlements.lockedBadge")}
                  </span>
                )}
                {selected && (
                  <span
                    style={{
                      position: "absolute",
                      top: "8px",
                      right: "8px",
                      width: "20px",
                      height: "20px",
                      borderRadius: RADII.pill,
                      background: c,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Check size={12} color="#fff" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
        {/* LinkedIn format toggle → segmented control */}
        {activeMode === "linkedin-post" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "14px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...fieldLbl, marginBottom: 0 }}>
              {tr("step1.platform.formatLabel")}
            </span>
            <div
              style={{
                display: "inline-flex",
                background: t.inputBg,
                borderRadius: RADII.control,
                padding: "4px",
                gap: "4px",
              }}
            >
              {[
                { v: "post", l: tr("step1.platform.format.post") },
                { v: "carousel", l: tr("step1.platform.format.carousel") },
              ].map(({ v, l }) => {
                const on = contentFormat === v;
                const locked = selectionLocked({
                  mode: "linkedin-post",
                  contentFormat: v,
                });
                return (
                  <button
                    key={v}
                    disabled={locked}
                    title={locked ? lockedHint : undefined}
                    onClick={locked ? undefined : () => set("contentFormat", v)}
                    style={{
                      padding: "10px 16px",
                      border: "none",
                      borderRadius: RADII.controlInner,
                      background: on ? t.surface : "transparent",
                      color: on ? t.text : t.textMuted,
                      fontSize: "13px",
                      fontWeight: 600,
                      fontFamily: FONT_SANS,
                      cursor: locked ? "not-allowed" : "pointer",
                      opacity: locked ? 0.5 : 1,
                      boxShadow: on ? SHADOW.card : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {locked && <LockMark />}
                    {l}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Social channel selector */}
        {activeMode === "social" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "12px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...fieldLbl, marginBottom: 0 }}>
              {tr("step1.platform.channelLabel")}
            </span>
            <div
              style={{
                display: "inline-flex",
                background: t.inputBg,
                borderRadius: RADII.control,
                padding: "4px",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              {SOCIAL_CHANNELS.map((ch) => {
                const on = socialChannel === ch.id;
                const locked = selectionLocked({
                  mode: "social",
                  socialChannel: ch.id,
                });
                return (
                  <button
                    key={ch.id}
                    disabled={locked}
                    title={locked ? lockedHint : undefined}
                    onClick={locked ? undefined : () => set("socialChannel", ch.id)}
                    style={{
                      padding: "10px 16px",
                      border: "none",
                      borderRadius: RADII.controlInner,
                      background: on ? t.surface : "transparent",
                      color: on ? t.text : t.textMuted,
                      fontSize: "13px",
                      fontWeight: 600,
                      fontFamily: FONT_SANS,
                      cursor: locked ? "not-allowed" : "pointer",
                      opacity: locked ? 0.5 : 1,
                      boxShadow: on ? SHADOW.card : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {locked && <LockMark />}
                    {tr(`channels.${ch.id}.name`)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Email channel selector */}
        {activeMode === "email" && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "14px",
              marginTop: "12px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ ...fieldLbl, marginBottom: 0 }}>
              {tr("step1.platform.channelLabel")}
            </span>
            <div
              style={{
                display: "inline-flex",
                background: t.inputBg,
                borderRadius: RADII.control,
                padding: "4px",
                gap: "4px",
                flexWrap: "wrap",
              }}
            >
              {EMAIL_CHANNELS.map((ch) => {
                const on = emailChannel === ch.id;
                const locked = selectionLocked({
                  mode: "email",
                  emailChannel: ch.id,
                });
                return (
                  <button
                    key={ch.id}
                    disabled={locked}
                    title={locked ? lockedHint : undefined}
                    onClick={locked ? undefined : () => set("emailChannel", ch.id)}
                    style={{
                      padding: "10px 16px",
                      border: "none",
                      borderRadius: RADII.controlInner,
                      background: on ? t.surface : "transparent",
                      color: on ? t.text : t.textMuted,
                      fontSize: "13px",
                      fontWeight: 600,
                      fontFamily: FONT_SANS,
                      cursor: locked ? "not-allowed" : "pointer",
                      opacity: locked ? 0.5 : 1,
                      boxShadow: on ? SHADOW.card : "none",
                      transition: "all 0.15s",
                    }}
                  >
                    {locked && <LockMark />}
                    {tr(`channels.${ch.id}.name`)}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        {/* Entitlement notices, in order of urgency:
            1. nothing is granted at all — say why, with the right remedy
            2. the CURRENT selection is refused — the run about to launch will 402
            3. something else is refused — a softer hint
            ⛔ The banner never hides a card: the catalogue stays discoverable
            and every entry is simply disabled. */}
        {blockedReason ? (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "14px",
              padding: "10px 14px",
              borderRadius: RADII.control,
              background: SEMANTIC.attenzione.bg,
              color: SEMANTIC.attenzione.fg,
              fontSize: "12px",
              lineHeight: 1.5,
              fontFamily: FONT_SANS,
            }}
          >
            <Lock size={14} />
            <span>{tr(`step1.platform.entitlements.reason.${blockedReason}`)}</span>
            {/* ⛔ NO upgrade link since 2026-09-24. We cannot say WHICH plan
                includes the refused output type, so sending the user to the
                catalogue leaves them to work it out. The sentence states the
                fact; the remedy line below names who can act.
                ⏰ Revisit at the UI redesign, when a card can say "su Pro". */}
            <span style={{ fontWeight: 600 }}>
              {tr("step1.platform.entitlements.contactAdmin")}
            </span>
          </div>
        ) : currentSelectionLocked ? (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
              marginTop: "14px",
              padding: "10px 14px",
              borderRadius: RADII.control,
              background: SEMANTIC.attenzione.bg,
              color: SEMANTIC.attenzione.fg,
              fontSize: "12px",
              lineHeight: 1.5,
              fontFamily: FONT_SANS,
            }}
          >
            <Lock size={14} />
            <span>
              {tr("step1.platform.entitlements.selectedLocked", {
                mode: tr(`modes.${activeMode}.name`, {
                  defaultValue: tr("step1.platform.fallbackModeName"),
                }),
              })}
            </span>
          </div>
        ) : anyOptionLocked ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexWrap: "wrap",
              marginTop: "14px",
              fontSize: "12px",
              lineHeight: 1.5,
              fontFamily: FONT_SANS,
              color: t.textMuted,
            }}
          >
            <Lock size={12} />
            <span>{tr("step1.platform.entitlements.someLocked")}</span>
          </div>
        ) : null}

        {/* A 503 on /entitlements: we are showing the LAST KNOWN answer, not a
            verdict. Never rendered as "you have none". */}
        {stale && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginTop: "8px",
              fontSize: "11px",
              lineHeight: 1.5,
              fontFamily: FONT_SANS,
              color: t.textMuted,
            }}
          >
            <span>{tr("step1.platform.entitlements.stale")}</span>
          </div>
        )}
      </div>

      {/* 1: Argomento / Tema / Soggetto */}
      <div id="cg-brief" style={sec}>
        <SectionHeader
          icon={BriefIcon}
          title={tr("step1.topic.cardTitle")}
          subtitle={tr("step1.topic.subtitle")}
        />
        <button
          type="button"
          onClick={() => importInputRef.current?.click()}
          title={tr("step1.topic.importTitle")}
          style={{
            display: "none",
            alignItems: "center",
            gap: "6px",
            padding: "6px 10px",
            fontSize: "11px",
            fontFamily: FONT_MONO,
            letterSpacing: "1px",
            textTransform: "uppercase",
            color: c,
            background: "transparent",
            border: `1px solid ${c}55`,
            borderRadius: "8px",
            cursor: "pointer",
          }}
        >
          <Download size={12} /> {tr("step1.topic.importButton")}
        </button>
        <input
          ref={importInputRef}
          type="file"
          accept=".md,.txt,.doc,.docx,.html,.htm,.json"
          style={{ display: "none" }}
          onChange={handleImportSelect}
        />
        {importStatus && (
          <div
            style={{
              fontSize: "12px",
              padding: "8px 10px",
              borderRadius: "8px",
              marginBottom: "10px",
              background:
                importStatus.type === "ok"
                  ? SEMANTIC.verde.bg
                  : SEMANTIC.errore.bg,
              color:
                importStatus.type === "ok" ? SEMANTIC.verde.fg : SEMANTIC.errore.fg,
              border: `1px solid ${
                importStatus.type === "ok"
                  ? SEMANTIC.verde.bg
                  : SEMANTIC.errore.bg
              }`,
            }}
          >
            {importStatus.msg}
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <label style={{ ...fieldLbl, marginBottom: 0 }}>
            {tr("step1.topic.title")}{" "}
            <span style={{ color: SEMANTIC.errore.fg }}>*</span>
          </label>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: FONT_SANS,
              color:
                (taskText || "").length >= 200
                  ? SEMANTIC.errore.fg
                  : COLORS.borderStrong,
            }}
          >
            {(taskText || "").length}/200
          </span>
        </div>
        <input
          type="text"
          value={taskText}
          maxLength={200}
          onChange={(e) =>
            dispatch({ type: "SET_TASK", value: e.target.value })
          }
          placeholder={tr("step1.topic.placeholder")}
          style={inp}
        />
        {taskContract &&
          (taskContract.intent ||
            taskContract.contentType ||
            taskContract.dates.length > 0) && (
            <div
              style={{
                display: "flex",
                gap: "6px",
                flexWrap: "wrap",
                marginTop: "8px",
              }}
            >
              {taskContract.intent && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "100px",
                    background: `${c}15`,
                    color: c,
                    fontSize: "11px",
                    fontWeight: 600,
                    border: `1px solid ${c}30`,
                  }}
                >
                  <Target
                    size={10}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "3px",
                    }}
                  />
                  {taskContract.intent}
                </span>
              )}
              {taskContract.contentType && (
                <span
                  style={{
                    padding: "3px 10px",
                    borderRadius: "100px",
                    background: "rgba(139,92,246,0.1)",
                    color: COLORS.accent,
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid rgba(139,92,246,0.2)",
                  }}
                >
                  <Layers
                    size={10}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "3px",
                    }}
                  />
                  {taskContract.contentType}
                </span>
              )}
              {taskContract.dates.map((d, i) => (
                <span
                  key={i}
                  style={{
                    padding: "3px 10px",
                    borderRadius: "100px",
                    background: SEMANTIC.verde.bg,
                    color: SEMANTIC.verde.fg,
                    fontSize: "11px",
                    fontWeight: 600,
                    border: "1px solid rgba(16,185,129,0.2)",
                  }}
                >
                  <Calendar
                    size={10}
                    style={{
                      display: "inline",
                      verticalAlign: "middle",
                      marginRight: "3px",
                    }}
                  />
                  {d.normalized}
                </span>
              ))}
            </div>
          )}

        <div style={{ ...fieldLbl, marginTop: "18px" }}>
          {tr("step1.topic.keyPointsTitle")}{" "}
          <Tooltip
            text={tr("step1.topic.keyPointsTooltip")}
            color={c}
          />
        </div>
        <AutoGrowTextarea
          value={state.keyPoints || ""}
          onChange={(e) => set("keyPoints", e.target.value)}
          maxHeight={AUTO_GROW_MAX_HEIGHT}
          style={{ ...inp, resize: "none", overflow: "hidden" }}
        />

        <div style={{ ...fieldLbl, marginTop: "14px" }}>
          {tr("step1.topic.angleTitle")}{" "}
          <Tooltip
            text={tr("step1.topic.angleTooltip")}
            color={c}
          />
        </div>
        <AutoGrowTextarea
          value={focusTopic}
          onChange={(e) => set("focusTopic", e.target.value)}
          maxHeight={AUTO_GROW_MAX_HEIGHT}
          style={{ ...inp, resize: "none", overflow: "hidden" }}
        />
      </div>

      {/* 6b: Parametri della modalità — sempre visibile (contiene Persona/Obiettivo/Tono).
          La sezione dei parametri specifici + i campi CTA sono nascosti per social. */}
      <div id="cg-parametri" style={sec}>
        <SectionHeader
          icon={ParametriIcon}
          title={tr("step1.params.cardTitle")}
          subtitle={tr("step1.params.subtitle")}
        />

        {/* Top params row — landing: 3 cols × 2 rows [Persona, Obiettivo, Tone] / [Formula Headline, Social Proof];
            altri mode: 3 cols [Persona, Obiettivo, Tone]. */}
        {(() => {
          const personaField = (
            <Sel
              key="persona"
              label={
                <>
                  {tr("step1.params.personaLabel")}{" "}
                  <Tooltip
                    text={tr("step1.params.personaTooltip")}
                    color={c}
                  />
                </>
              }
              value={persona}
              onChange={(v) => set("persona", v)}
              options={trOptions("persona", personaOptions)}
            />
          );
          const objectiveField = (
            <Sel
              key="objective"
              label={
                <>
                  {tr("step1.params.objectiveLabel")}{" "}
                  <Tooltip
                    text={tr("step1.params.objectiveTooltip")}
                    color={c}
                  />
                </>
              }
              value={objective}
              onChange={(v) => set("objective", v)}
              options={trOptions("objective", objectiveOptions)}
            />
          );
          const toneField = (
            <div key="tone" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <Sel
                label={
                  <>
                    {tr("step1.params.toneLabel")}{" "}
                    <Tooltip
                      text={tr("step1.params.toneTooltip")}
                      color={c}
                    />
                  </>
                }
                value={tone}
                onChange={(v) => set("tone", v)}
                options={toneOptions.map(o =>
                  o.value === "custom" && customTone?.trim()
                    ? { ...o, label: `${tr("step1.params.toneCustomPrefix")}${customTone.trim().length > 60 ? customTone.trim().slice(0, 59) + "…" : customTone.trim()}` }
                    : { ...o, label: resolveToneLabel(tr, o.value, toneOptions) }
                )}
              />
              {tone === "custom" && (
                <>
                  <AutoGrowTextarea
                    value={customTone}
                    onChange={(e) => set("customTone", e.target.value)}
                    placeholder={tr("step1.params.toneCustomPlaceholder")}
                    maxHeight={AUTO_GROW_MAX_HEIGHT}
                    style={{ ...inp, padding: "10px 14px", resize: "none", overflow: "hidden" }}
                  />
                  <div
                    style={{
                      fontSize: "12px",
                      color: t.textMuted,
                      fontStyle: "italic",
                      lineHeight: 1.5,
                      fontFamily: FONT_SANS,
                    }}
                  >
                    {tr("step1.params.toneHelper")}
                  </div>
                </>
              )}
            </div>
          );
          const formulaHeadlineField = (
            <Sel
              key="formula-headline"
              label={tr("step1.params.formulaHeadlineLabel")}
              value={landingParams.headlineFormula}
              onChange={(v) =>
                set("landingParams", {
                  ...landingParams,
                  headlineFormula: v,
                })
              }
              options={trOptions("landingHeadline", LANDING_HEADLINE_OPTIONS)}
            />
          );
          const socialProofField = (
            <div key="social-proof" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <label style={{ ...fieldLbl, marginBottom: 0 }}>
                {tr("step1.params.socialProofLabel")}{" "}
                <Tooltip
                  text={tr("step1.params.socialProofTooltip")}
                  color={c}
                />
              </label>
              <input
                type="text"
                value={landingParams.socialProof}
                onChange={(e) =>
                  set("landingParams", {
                    ...landingParams,
                    socialProof: e.target.value,
                  })
                }
                placeholder={tr("step1.params.socialProofPlaceholder")}
                style={inp}
              />
            </div>
          );
          const lengthField = (
            <LengthSlider
              key="post-length"
              label={tr("step1.params.postLengthLabel")}
              options={trOptions("postLength", POST_LENGTH_OPTIONS)}
              value={linkedInParams.postLength}
              onChange={(v) =>
                set("linkedInParams", {
                  ...linkedInParams,
                  postLength: v,
                })
              }
            />
          );
          const articleLengthField = (
            <LengthSlider
              key="article-length"
              label={tr("step1.params.articleLengthLabel")}
              options={trOptions("blogLength", BLOG_LENGTH_OPTIONS)}
              value={blogParams.articleLength}
              onChange={(v) =>
                set("blogParams", { ...blogParams, articleLength: v })
              }
            />
          );
          const emailLengthField = (
            <LengthSlider
              key="email-length"
              label={tr("step1.params.emailLengthLabel")}
              options={trOptions("emailLength", EMAIL_LENGTH_OPTIONS)}
              value={emailParams.emailLength}
              onChange={(v) =>
                set("emailParams", { ...emailParams, emailLength: v })
              }
            />
          );
          // Length is one control across every channel that has one: the same
          // LengthSlider, the same "Nome (intervallo)" label shape, and the same
          // first slot in the params grid — [Lunghezza | Tono] / [Persona | Obiettivo].
          // Only the option set differs per channel; the stored values stay the wire
          // strings ('short', '1500-2000', 'standard'), untouched.
          // Social has no length parameter, so it keeps the plain three-field grid.
          const LENGTH_FIELD_BY_MODE = {
            "linkedin-post": lengthField,
            "blog-seo": articleLengthField,
            "email": emailLengthField,
          };
          const modeLengthField = LENGTH_FIELD_BY_MODE[activeMode];
          const isLanding = activeMode === "landing";
          const fields = isLanding
            ? [personaField, objectiveField, toneField, formulaHeadlineField, socialProofField]
            : modeLengthField
              ? [modeLengthField, toneField, personaField, objectiveField]
              : [personaField, objectiveField, toneField];
          return (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                alignItems: "start",
                gap: "12px 24px",
                marginBottom: "12px",
              }}
            >
              {fields}
            </div>
          );
        })()}

        {/* Parametri specifici della modalità + CTA (nascosti per social) */}
        {!["social"].includes(activeMode) && (
          <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
            }}
          >
            {/* Slide count is the carousel's length, so it gets the same slider.
                It stays below rather than in the grid because that mode already
                shows the post-length slider in the grid's first slot. */}
            {activeMode === "linkedin-post" && contentFormat === "carousel" && (
              <LengthSlider
                label={tr("step1.params.slideCountLabel")}
                value={carouselParams.slideCount}
                onChange={(v) =>
                  set("carouselParams", { ...carouselParams, slideCount: v })
                }
                options={trOptions("carouselSlide", CAROUSEL_SLIDE_OPTIONS)}
              />
            )}
            {activeMode === "blog-seo" && (
              <>
                {/* Lunghezza Articolo now lives in the params grid above, as the
                    LinkedIn post-length slider does. */}
                <Sel
                  label={tr("step1.params.seoIntentLabel")}
                  value={blogParams.seoIntent}
                  onChange={(v) =>
                    set("blogParams", { ...blogParams, seoIntent: v })
                  }
                  options={trOptions("seoIntent", SEO_INTENT_OPTIONS)}
                />
                <div style={{ gridColumn: "1/-1" }}>
                  <label style={{ ...fieldLbl, marginBottom: "8px" }}>
                    {tr("step1.params.keywordsLabel")}
                  </label>

                  {/* [FIX-v161] Prima era una griglia 1fr/1fr con 1 campo a
                      sinistra e 3 impilati a destra: la colonna sinistra
                      restava vuota per due terzi. Ora Principale occupa la riga
                      intera e le tre Secondarie stanno affiancate sotto, così
                      le baseline dei campi si allineano. */}
                  <div style={{ marginBottom: "12px" }}>
                    <label style={{ ...fieldLbl, marginBottom: "4px" }}>
                      {tr("step1.params.keywordsPrimaryLabel")}
                    </label>
                    <input
                      value={blogParams.keywords.primary}
                      onChange={(e) => setPrimaryKeyword(e.target.value)}
                      placeholder={tr("step1.params.keywordsPrimaryPlaceholder")}
                      style={inp}
                    />
                  </div>

                  <div>
                    <label style={{ ...fieldLbl, marginBottom: "4px" }}>
                      {tr("step1.params.keywordsSecondaryLabel")}
                    </label>
                    {/* auto-fit, non repeat(3): sotto i 980px la griglia della
                        pagina collassa a una colonna e tre campi fissi con
                        padding 16px scendono a ~100px l'uno, inusabili. Con
                        auto-fit vanno a capo da soli. */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                        gap: "8px",
                      }}
                    >
                      {[0, 1, 2].map((idx) => (
                        <input
                          key={idx}
                          value={
                            blogParams.keywords.secondary[idx]?.keyword || ""
                          }
                          onChange={(e) => setSecondarySlot(idx, e.target.value)}
                          placeholder={tr("step1.params.keywordsSecondaryPlaceholder")}
                          style={inp}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
            {activeMode === "email" && (
              <>
                {/* Lunghezza now lives in the params grid above, as the LinkedIn
                    post-length slider does. */}
                {emailChannel === "email-classic" && (
                  <Sel
                    label={tr("step1.params.subjectStyleLabel")}
                    value={emailParams.subjectStyle}
                    onChange={(v) =>
                      set("emailParams", { ...emailParams, subjectStyle: v })
                    }
                    options={trOptions("subjectStyle", SUBJECT_STYLE_OPTIONS)}
                  />
                )}
                {emailChannel === "linkedin-newsletter" && (
                  <Sel
                    label={tr("step1.params.articleStyleLabel")}
                    helper={tr("step1.params.articleStyleHelper")}
                    value={emailParams.articleStyle}
                    onChange={(v) =>
                      set("emailParams", { ...emailParams, articleStyle: v })
                    }
                    options={trOptions("articleStyle", ARTICLE_STYLE_OPTIONS)}
                  />
                )}
              </>
            )}
            {activeMode === "ads" && (
              <>
                <Sel
                  label={tr("step1.params.adsChannelLabel")}
                  value={adsParams.channel}
                  onChange={(v) =>
                    set("adsParams", { ...adsParams, channel: v })
                  }
                  options={trOptions("adsChannel", ADS_CHANNEL_OPTIONS)}
                />
                <Sel
                  label={tr("step1.params.adsGroupsLabel")}
                  value={adsParams.adGroups}
                  onChange={(v) =>
                    set("adsParams", { ...adsParams, adGroups: v })
                  }
                  options={trOptions("adGroups", [
                    { value: "1" },
                    { value: "2" },
                    { value: "3" },
                  ])}
                />
              </>
            )}
            {activeMode === "case-study" && (
              <>
                <Sel
                  label={tr("step1.params.caseStudyStyleLabel")}
                  value={caseStudyParams.outputStyle}
                  onChange={(v) =>
                    set("caseStudyParams", {
                      ...caseStudyParams,
                      outputStyle: v,
                    })
                  }
                  options={trOptions("caseStudySection", CASE_STUDY_SECTION_OPTIONS)}
                />
                <Sel
                  label={tr("step1.params.caseStudyFocusLabel")}
                  value={caseStudyParams.focusArea}
                  onChange={(v) =>
                    set("caseStudyParams", { ...caseStudyParams, focusArea: v })
                  }
                  options={trOptions("caseStudyFocus", CASE_STUDY_FOCUS_OPTIONS)}
                />
              </>
            )}
          </div>
          {/* Includi CTA toggle → reveals Tipo / Testo / URL CTA */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginTop: "16px",
            }}
          >
            <label style={{ ...fieldLbl, marginBottom: 0 }}>
              {tr("step1.params.includeCtaLabel")}{" "}
              <Tooltip
                text={tr("step1.params.includeCtaTooltip")}
                color={c}
              />
            </label>
            <Toggle on={includeCta} onToggle={toggleCta} />
          </div>
          {includeCta && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: "12px",
                marginTop: "12px",
              }}
            >
              {activeMode === "linkedin-post" && (
                <Sel
                  label={tr("step1.params.ctaTypeLabel")}
                  value={linkedInParams.ctaType}
                  onChange={(v) =>
                    set("linkedInParams", { ...linkedInParams, ctaType: v })
                  }
                  options={trOptions("ctaType", CTA_TYPE_OPTIONS)}
                />
              )}
              <div>
                <label style={fieldLbl}>{tr("step1.params.ctaTextLabel")}</label>
                <input
                  type="text"
                  value={ctaText}
                  onChange={(e) => set("ctaText", e.target.value)}
                  placeholder={tr("step1.params.ctaTextPlaceholder")}
                  style={inp}
                />
              </div>
              <div>
                <label style={fieldLbl}>{tr("step1.params.ctaUrlLabel")}</label>
                <input
                  type="url"
                  value={ctaUrl}
                  onChange={(e) => set("ctaUrl", e.target.value)}
                  onBlur={(e) => {
                    const n = normalizeUrl(e.target.value);
                    if (n !== e.target.value) set("ctaUrl", n);
                  }}
                  placeholder={tr("step1.params.ctaUrlPlaceholder")}
                  style={inp}
                />
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* 2: Ricerca e Fonti */}
      <div id="cg-fonti" style={sec}>
        <SectionHeader
          icon={FontiIcon}
          title={tr("step1.sources.title")}
          subtitle={tr("step1.sources.subtitle")}
        />
        <>
          {/* Fonti prioritarie — viola (merge: documenti caricati + URL di riferimento) */}
          <SourceRow
            sem={SEMANTIC.viola}
            icon={Upload}
            title={tr("step1.sources.priority.label")}
            desc={tr("step1.sources.priority.helper")}
            on={priorityOn}
            onToggle={togglePriority}
          >
            {uploadBundle.files.length > 0 && (
              <input
                type="text"
                value={userKbQuery}
                onChange={(e) => set("userKbQuery", e.target.value)}
                placeholder={tr("step1.sources.upload.placeholder")}
                style={{ ...inp, marginBottom: "8px" }}
              />
            )}
            {priorityOn && (
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  position: "relative",
                }}
              >
                {/* Loading overlay */}
                {uploadBundle.isLoading && (
                  <div
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 10,
                      background: t.surface + "e6",
                      borderRadius: "10px",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        width: "28px",
                        height: "28px",
                        border: `3px solid ${c}30`,
                        borderTop: `3px solid ${c}`,
                        borderRadius: "50%",
                        animation: "spin 0.8s linear infinite",
                      }}
                    />
                    <span
                      style={{ fontSize: "12px", fontWeight: 600, color: c }}
                    >
                      {tr("step1.sources.upload.loading")}
                    </span>
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                  </div>
                )}
                {/* Empty-state hint — appare quando ON ma 0 file */}
                {uploadBundle.files.length === 0 && !uploadBundle.isLoading && (
                  <div
                    style={{
                      fontSize: "12px",
                      color: t.textFaint,
                      fontStyle: "italic",
                      marginBottom: "4px",
                      fontFamily: FONT_SANS,
                    }}
                  >
                    {tr("step1.sources.upload.emptyHint")}
                  </div>
                )}
                {/* Drop zone */}
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onClick={() =>
                    !uploadBundle.isLoading && fileInputRef.current?.click()
                  }
                  style={{
                    border: `2px dashed ${dragOver ? c : t.inputBorder}`,
                    borderRadius: "10px",
                    padding: "20px",
                    textAlign: "center",
                    cursor: uploadBundle.isLoading ? "not-allowed" : "pointer",
                    background: dragOver ? `${c}08` : t.inputBg,
                    transition: "all 0.2s",
                    opacity: uploadBundle.isLoading ? 0.4 : 1,
                  }}
                >
                  <Upload
                    size={20}
                    style={{
                      color: dragOver ? c : t.textFaint,
                      marginBottom: "6px",
                    }}
                  />
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: dragOver ? c : t.text,
                    }}
                  >
                    {tr("step1.sources.upload.dropTitle")}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: t.textFaint,
                      marginTop: "4px",
                    }}
                  >
                    {tr("step1.sources.upload.dropSubtitle")}
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  accept=".pdf,.docx,.pptx,.xlsx,.txt,.md"
                />
                {uploadBundle.error && (
                  <div style={{ color: SEMANTIC.errore.fg, fontSize: "12px" }}>
                    {uploadBundle.error}
                  </div>
                )}
                {uploadBundle.files.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "6px",
                    }}
                  >
                    {uploadBundle.files.map((f) => (
                      <div
                        key={f.filename}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 10px",
                          borderRadius: "8px",
                          background: t.inputBg,
                          border: `1px solid ${t.inputBorder}`,
                          fontSize: "12px",
                          color: t.text,
                        }}
                      >
                        <FileText
                          size={14}
                          style={{ color: c, flexShrink: 0 }}
                        />
                        <span
                          style={{
                            flex: 1,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {f.filename}
                        </span>
                        {f.chunks != null && (
                          <span
                            style={{
                              color: t.textFaint,
                              fontSize: "10px",
                              flexShrink: 0,
                            }}
                          >
                            {tr("step1.sources.upload.chunks", { count: f.chunks })}
                          </span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFile(f.filename);
                          }}
                          disabled={uploadBundle.isLoading}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: t.textFaint,
                            padding: "2px",
                            flexShrink: 0,
                            opacity: uploadBundle.isLoading ? 0.4 : 1,
                          }}
                          title={tr("step1.sources.upload.removeFileTitle")}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            {/* Link web di riferimento (max 3) */}
            <div style={{ marginTop: "14px" }}>
              <label style={fieldLbl}>{tr("step1.sources.refs.label")}</label>
              <div style={{ display: "flex", gap: "8px" }}>
                {referenceUrls.map((url, i) => (
                  <input
                    key={i}
                    type="url"
                    value={url}
                    onChange={(e) => {
                      if (!refsEnabled) set("refsEnabled", true);
                      dispatch({
                        type: "SET_REFERENCE_URL_AND_INVALIDATE",
                        index: i,
                        value: e.target.value,
                      });
                    }}
                    onBlur={(e) => {
                      const n = normalizeUrl(e.target.value);
                      if (n !== e.target.value)
                        dispatch({
                          type: "SET_REFERENCE_URL_AND_INVALIDATE",
                          index: i,
                          value: n,
                        });
                    }}
                    placeholder={tr("step1.sources.refs.placeholder")}
                    style={{ ...inp, flex: 1 }}
                  />
                ))}
              </div>
            </div>
          </SourceRow>
          {/* Knowledge Base Aziendale — verde */}
          <SourceRow
            sem={SEMANTIC.verde}
            icon={BookOpen}
            title={tr("step1.sources.kb.label")}
            desc={tr("step1.sources.kb.helper")}
            on={kbSearch.enabled}
            onToggle={() =>
              set("kbSearch", { ...kbSearch, enabled: !kbSearch.enabled })
            }
          >
            <input
              type="text"
              value={kbSearch.query}
              onChange={(e) =>
                set("kbSearch", { ...kbSearch, query: e.target.value })
              }
              placeholder={tr("step1.sources.kb.placeholder")}
              style={inp}
            />
          </SourceRow>
          {/* Ricerca web — info */}
          <SourceRow
            sem={SEMANTIC.info}
            icon={Globe}
            title={tr("step1.sources.web.label")}
            desc={tr("step1.sources.web.helper")}
            on={webSearch.enabled}
            onToggle={() =>
              set("webSearch", { ...webSearch, enabled: !webSearch.enabled })
            }
          >
            <input
              type="text"
              value={webSearch.query}
              onChange={(e) =>
                set("webSearch", { ...webSearch, query: e.target.value })
              }
              placeholder={tr("step1.sources.web.placeholder")}
              style={inp}
            />
          </SourceRow>
        </>
      </div>

    </div>
  );
};
export default Step1Setup;
